import { createSlice } from '@reduxjs/toolkit'

export interface DashboardSummary {
    totalUsers: number;
    totalTransactions: number;
    totalAmount: string;
    totalActiveSubscriptions: number;
    monthlyActiveSubscriptions: number;
    yearlyActiveSubscriptions: number;
    daily_users: number;
    weekly_users: number;
    monthly_users: number;
    totalFaceScans: number;
}

interface UserGraphPoint {
    date: string
    count: number
}

interface TransactionGraphPoint {
    date: string
    count: number
    amount: number
}
interface DashboardState {
    summary: DashboardSummary;
    userGraph: UserGraphPoint[]
    transactionGraph: TransactionGraphPoint[]
    loading: boolean;
    userGraphLoading: boolean;
    transactionGraphLoading: boolean
    error: string;
    userGraphError: string;
    transactionGraphError: string;
    graphFilterRange: number | string
    summaryFilterRange: number | 'all'
    activeUsers: {
        daily_users: number,
        weekly_users: number,
        monthly_users: number
    }
    activeUserLoading: boolean
}

const initialState: DashboardState = {
    summary: {
        totalUsers: 0,
        totalTransactions: 0,
        totalAmount: '0.00',
        totalActiveSubscriptions: 0,
        totalFaceScans: 0,
        monthlyActiveSubscriptions: 0,
        yearlyActiveSubscriptions: 0,
        daily_users: 0,
        weekly_users: 0,
        monthly_users: 0
    },
    activeUsers: {
        daily_users: 0,
        weekly_users: 0,
        monthly_users: 0
    },
    userGraph: [],
    transactionGraph: [],
    loading: false,
    userGraphLoading: false,
    transactionGraphLoading: false,
    error: '',
    userGraphError: '',
    transactionGraphError: '',
    graphFilterRange: 7,
    summaryFilterRange: 7,
    activeUserLoading: false
}

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        fetchStart(state) {
            state.loading = true
            state.error = ''
        },
        fetchSuccess(state, action) {
            state.loading = false
            state.summary = {
                ...state.summary,
                ...action.payload
            }
        },
        fetchFailure(state, action) {
            state.loading = false
            state.error = action.payload
        },
        userGraphStart(state) {
            state.userGraphLoading = true
            state.userGraphError = ''
        },
        userGraphSuccess(state, action) {
            state.userGraphLoading = false
            state.userGraph = action.payload
        },
        userGraphFailure(state, action) {
            state.userGraphLoading = false
            state.userGraphError = action.payload
        },
        transactionGraphStart(state) {
            state.transactionGraphLoading = true
            state.transactionGraphError = ''
        },
        transactionGraphSuccess(state, action) {
            state.transactionGraphLoading = false
            state.transactionGraph = action.payload
        },
        transactionGraphFailure(state, action) {
            state.transactionGraphLoading = false
            state.transactionGraphError = action.payload
        },
        setGraphFilterRange(state, action) {
            state.graphFilterRange = action.payload
        },
        setSummaryFilterRange(state, action) {
            state.summaryFilterRange = action.payload
        },
        setActiveUsersLoading(state, action) {
            state.activeUserLoading = action.payload
        },
        setActiveUsers(state, action) {
            state.activeUsers = action.payload
        },
        resetDashboard(state) {
            Object.assign(state, initialState)
        }
    }
})

export const {
    fetchStart,
    fetchSuccess,
    fetchFailure,
    userGraphFailure,
    userGraphStart,
    userGraphSuccess,
    transactionGraphFailure,
    transactionGraphStart,
    transactionGraphSuccess,
    setGraphFilterRange,
    setSummaryFilterRange,
    setActiveUsers,
    setActiveUsersLoading,
    resetDashboard
} = dashboardSlice.actions

export default dashboardSlice.reducer
